/**
 * The media library as a folder tree — mock implementation.
 *
 * Separate from `media.api.ts` on purpose. That module models the *upload*
 * pipeline (presign → PUT → complete) and its store starts empty, because
 * everything in it is something the user just uploaded this session. This one
 * models *browsing what already exists*, which is a different request and has
 * to return content on first paint or the picker is an empty box.
 *
 * The real endpoint will be:
 *   GET /api/v1/media/tree?brandId=… → MediaFolder
 *
 * Folders do not exist on `MediaAsset` yet. When they land, replace
 * `MOCK_TREE` with the response and keep these shapes.
 */

import type { MediaAssetType } from '@app/common';

export interface MediaLibraryFile {
  id: string;
  name: string;
  type: MediaAssetType;
  /** Thumbnail/preview source. A data URI in the mock, a CDN URL in production. */
  previewUrl: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

export interface MediaFolder {
  id: string;
  name: string;
  folders: MediaFolder[];
  files: MediaLibraryFile[];
}

// ─── Thumbnail generation ────────────────────────────────────────────────────

/**
 * Inline SVG rather than checked-in binaries: the picker needs ~20 visually
 * distinct thumbnails and a data URI costs no network request and no repo
 * weight. Every one is deterministic, so the tree looks the same each load.
 */
function tile(from: string, to: string, glyph: string, video = false): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${from}"/><stop offset="100%" stop-color="${to}"/>
    </linearGradient></defs>
    <rect width="120" height="120" fill="url(#g)"/>
    <text x="60" y="76" text-anchor="middle" font-family="Inter,Arial,sans-serif" font-size="46" font-weight="700" fill="#ffffff" opacity="0.85">${glyph}</text>
    ${video ? '<circle cx="60" cy="60" r="26" fill="#000" opacity="0.35"/><path d="M52 46 L80 60 L52 74 Z" fill="#fff" opacity="0.9"/>' : ''}
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const MB = 1024 * 1024;

function image(
  id: string,
  name: string,
  from: string,
  to: string,
  glyph: string,
  sizeMb: number
): MediaLibraryFile {
  return {
    id,
    name,
    type: 'image',
    previewUrl: tile(from, to, glyph),
    sizeBytes: Math.round(sizeMb * MB),
    width: 1200,
    height: 1200,
    durationMs: null,
  };
}

function video(
  id: string,
  name: string,
  from: string,
  to: string,
  seconds: number,
  sizeMb: number
): MediaLibraryFile {
  return {
    id,
    name,
    type: 'video',
    previewUrl: tile(from, to, '', true),
    sizeBytes: Math.round(sizeMb * MB),
    width: 1920,
    height: 1080,
    durationMs: seconds * 1000,
  };
}

/**
 * The launch folder's assets get real artwork rather than a letter tile: they
 * are what the composer opens with, so they appear in the picker, the media
 * strip and the preview at once, and three identical grey squares there would
 * make the whole screen look unfinished.
 */
const LAUNCH_POSTER = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
  <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="#0f172a"/><stop offset="55%" stop-color="#1e293b"/><stop offset="100%" stop-color="#1e3a8a"/>
  </linearGradient></defs>
  <rect width="400" height="400" fill="url(#g)"/>
  <circle cx="330" cy="90" r="120" fill="#3b82f6" opacity="0.25"/>
  <rect x="250" y="150" width="110" height="210" rx="18" fill="#0b1220" stroke="#475569" stroke-width="3"/>
  <rect x="260" y="162" width="90" height="186" rx="12" fill="#1d4ed8" opacity="0.75"/>
  <text x="34" y="196" fill="#ffffff" font-family="Inter,Arial,sans-serif" font-size="44" font-weight="800">FUTURE</text>
  <text x="34" y="248" fill="#ffffff" font-family="Inter,Arial,sans-serif" font-size="44" font-weight="800">IS HERE</text>
  <rect x="34" y="272" width="120" height="5" rx="2.5" fill="#60a5fa"/>
</svg>`)}`;

const PRODUCT_SHOT = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
  <defs><linearGradient id="p" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="#6d28d9"/><stop offset="50%" stop-color="#4f46e5"/><stop offset="100%" stop-color="#1e3a8a"/>
  </linearGradient></defs>
  <rect width="400" height="400" fill="url(#p)"/>
  <circle cx="90" cy="320" r="150" fill="#a78bfa" opacity="0.28"/>
  <rect x="140" y="60" width="130" height="250" rx="24" fill="#0f172a" stroke="#818cf8" stroke-width="4"/>
  <rect x="152" y="74" width="106" height="222" rx="16" fill="#312e81"/>
  <circle cx="205" cy="112" r="16" fill="#c7d2fe" opacity="0.6"/>
</svg>`)}`;

const CLIP_THUMB = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
  <defs><linearGradient id="v" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="#111827"/><stop offset="100%" stop-color="#374151"/>
  </linearGradient></defs>
  <rect width="400" height="400" fill="url(#v)"/>
  <circle cx="200" cy="200" r="70" fill="#000000" opacity="0.35"/>
  <path d="M178 162 L252 200 L178 238 Z" fill="#ffffff" opacity="0.9"/>
</svg>`)}`;

// ─── The tree ────────────────────────────────────────────────────────────────

const MOCK_TREE: MediaFolder = {
  id: 'root',
  name: 'Media Library',
  files: [
    image('f-logo', 'brand-logo.png', '#1d4ed8', '#3b82f6', 'L', 0.4),
    image('f-cover', 'page-cover.jpg', '#0f172a', '#334155', 'C', 1.9),
  ],
  folders: [
    {
      id: 'folder-campaigns',
      name: 'Campaigns',
      files: [],
      folders: [
        {
          id: 'folder-launch',
          name: 'Product Launch',
          folders: [],
          files: [
            { ...image('f-launch-1', 'future-is-here.png', '#0f172a', '#1e3a8a', 'F', 2.4), previewUrl: LAUNCH_POSTER },
            { ...image('f-launch-2', 'product-hero.png', '#6d28d9', '#1e3a8a', 'P', 1.8), previewUrl: PRODUCT_SHOT },
            image('f-launch-3', 'feature-grid.png', '#0369a1', '#0ea5e9', 'G', 1.2),
            { ...video('f-launch-4', 'teaser-clip.mp4', '#111827', '#374151', 15, 18.6), previewUrl: CLIP_THUMB },
            video('f-launch-5', 'unboxing-full.mp4', '#1f2937', '#4b5563', 96, 84.2),
          ],
        },
        {
          id: 'folder-summer',
          name: 'Summer Sale',
          folders: [],
          files: [
            image('f-summer-1', 'sale-banner.png', '#f59e0b', '#ef4444', 'S', 1.1),
            image('f-summer-2', 'discount-tag.png', '#f97316', '#dc2626', '%', 0.6),
            video('f-summer-3', 'sale-promo.mp4', '#7c2d12', '#c2410c', 22, 26.4),
          ],
        },
      ],
    },
    {
      id: 'folder-product',
      name: 'Product Shots',
      folders: [],
      files: [
        image('f-prod-1', 'device-front.png', '#312e81', '#4f46e5', '1', 2.2),
        image('f-prod-2', 'device-side.png', '#3730a3', '#6366f1', '2', 2.1),
        image('f-prod-3', 'device-back.png', '#1e1b4b', '#4338ca', '3', 2.3),
        image('f-prod-4', 'in-hand.png', '#0f766e', '#14b8a6', '4', 1.7),
      ],
    },
    {
      id: 'folder-team',
      name: 'Team & Culture',
      folders: [],
      files: [
        image('f-team-1', 'office-wide.jpg', '#065f46', '#10b981', 'O', 3.4),
        image('f-team-2', 'standup.jpg', '#155e75', '#22d3ee', 'T', 2.8),
        video('f-team-3', 'day-in-the-life.mp4', '#134e4a', '#0f766e', 47, 52.1),
      ],
    },
    {
      id: 'folder-ugc',
      name: 'Customer Photos',
      folders: [],
      files: [
        image('f-ugc-1', 'review-alex.jpg', '#9d174d', '#ec4899', 'A', 1.4),
        image('f-ugc-2', 'review-sam.jpg', '#831843', '#f472b6', 'S', 1.6),
        image('f-ugc-3', 'review-jo.jpg', '#701a75', '#c026d3', 'J', 1.3),
      ],
    },
  ],
};

export async function fetchMediaTree(): Promise<MediaFolder> {
  await new Promise((resolve) => setTimeout(resolve, 250));
  return MOCK_TREE;
}

/** Looks a file up anywhere in the tree. Returns null when the id is unknown. */
export function findLibraryFile(id: string): MediaLibraryFile | null {
  const walk = (folder: MediaFolder): MediaLibraryFile | null => {
    const hit = folder.files.find((f) => f.id === id);
    if (hit) return hit;
    for (const child of folder.folders) {
      const nested = walk(child);
      if (nested) return nested;
    }
    return null;
  };
  return walk(MOCK_TREE);
}

/** Folders open on first paint, so the picker is not a wall of closed rows. */
export const DEFAULT_EXPANDED_FOLDER_IDS = ['root', 'folder-campaigns', 'folder-launch'];
